/**
 * Intercepteur global de fetch IA.
 * Détecte automatiquement les appels aux endpoints IA et les journalise
 * dans aiRequestTracker pour alimenter le feed ONY du Dashboard.
 */

import { getChannelInfo } from './aiRequestTracker';
import { createTask, updateTask } from './aiTaskManager';

const AI_URL_PATTERNS = [
  /\/api\/nova/i,
  /\/api\/project\/[^/]+\/(analyze|generate-input)/i,
  /\/api\/ollama/i,
  /ollama/i,
  /:11434\/api/i
];

const URL_CHANNEL_MAP = [
  { pattern: /\/api\/nova\/chat\/stream/i, channel: 'nova-chat' },
  { pattern: /\/api\/nova\/chat/i, channel: 'nova-chat' },
  { pattern: /\/api\/nova\/stream/i, channel: 'content-generation' },
  { pattern: /\/api\/nova\/generate-email-template/i, channel: 'email-template' },
  { pattern: /\/api\/nova\/generate-json/i, channel: 'generate-json' },
  { pattern: /\/api\/nova\/analyze-file/i, channel: 'document-analysis' },
  { pattern: /\/api\/nova\/web-search/i, channel: 'web-search' },
  { pattern: /\/api\/nova\/npc-dialogue/i, channel: 'npc' },
  { pattern: /\/api\/nova\/code\/(analyze|optimize|generate)/i, channel: 'code-editor' },
  { pattern: /\/api\/nova\/regex\/generate/i, channel: 'code-editor' },
  { pattern: /\/api\/nova\/transform/i, channel: 'file-transform' },
  { pattern: /\/api\/nova\/context/i, channel: 'conversation-context' },
  { pattern: /\/api\/nova\/actions/i, channel: 'nova-actions' },
  { pattern: /\/api\/nova\/excel/i, channel: 'excel-ai' },
  { pattern: /\/api\/nova\/welcome/i, channel: 'nova-chat' },
  { pattern: /\/api\/nova\/weather/i, channel: 'weather-automation' },
  { pattern: /\/api\/project\/[^/]+\/analyze/i, channel: 'project-analyzer' },
  { pattern: /\/api\/project\/[^/]+\/generate-input/i, channel: 'project-analyzer' },
  { pattern: /ollama|api\/generate|api\/tags/i, channel: 'scene-analyzer' }
];

function isAiUrl(url) {
  if (!url) return false;
  return AI_URL_PATTERNS.some(p => p.test(url));
}

function resolveChannel(url, body) {
  if (body?.channel) return body.channel;

  for (const { pattern, channel } of URL_CHANNEL_MAP) {
    if (pattern.test(url)) return channel;
  }

  return 'generic';
}

function getRequestText(body) {
  if (!body) return '';
  const candidates = [
    body.message,
    body.prompt,
    body.query,
    body.content,
    body.description,
    body.command,
    body.text
  ];
  for (const c of candidates) {
    if (c && typeof c === 'string') return c;
  }
  return body.name ? `Création: ${body.name}` : '';
}

async function extractBody(resource, options) {
  try {
    if (typeof resource !== 'string') {
      const clone = resource.clone();
      const contentType = clone.headers?.get('content-type') || '';
      if (contentType.includes('multipart/form-data')) {
        const fd = await clone.formData();
        const obj = {};
        for (const [key, value] of fd.entries()) {
          obj[key] = value instanceof File ? value.name : value;
        }
        return obj;
      }
      const text = await clone.text();
      return text ? JSON.parse(text) : {};
    }

    const body = options?.body;
    if (body instanceof FormData) {
      const obj = {};
      for (const [key, value] of body.entries()) {
        obj[key] = value instanceof File ? value.name : value;
      }
      return obj;
    }
    if (typeof body === 'string') {
      return body ? JSON.parse(body) : {};
    }
    return {};
  } catch {
    return {};
  }
}

async function safeReadResponseText(response) {
  try {
    const contentType = response.headers?.get('content-type') || '';
    if (contentType.includes('text/event-stream')) return null;
    if (!contentType.includes('application/json') && !contentType.includes('text/')) return null;

    const clone = response.clone();
    const text = await clone.text();
    if (text.startsWith('{') || text.startsWith('[')) {
      const parsed = JSON.parse(text);
      return parsed.response || parsed.message || parsed.result || parsed.data || parsed.template || JSON.stringify(parsed).slice(0, 200);
    }
    return text.slice(0, 200);
  } catch {
    return null;
  }
}

function isStreamRequest(headers, body) {
  if (body && (body.stream === true || body.stream === 'true')) return true;
  if (headers && headers['Content-Type']?.includes('text/event-stream')) return true;
  return false;
}

function appendTaskIdToUrl(url, taskId) {
  try {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}taskId=${encodeURIComponent(taskId)}`;
  } catch {
    return url;
  }
}

function buildContextFromBody(body) {
  if (!body || typeof body !== 'object') return {};
  const keep = ['channel', 'type', 'model', 'filename', 'tool', 'mode', 'action', 'language', 'target', 'url', 'options', 'extra'];
  const context = {};
  for (const key of keep) {
    if (body[key] !== undefined) context[key] = body[key];
  }
  // Historique de conversation quand il est présent
  if (Array.isArray(body.messages)) context.messages = body.messages;
  if (Array.isArray(body.history)) context.history = body.history;
  return context;
}

function buildHistoryFromBody(body, requestText) {
  if (!body || typeof body !== 'object') return requestText ? [{ role: 'user', content: requestText }] : [];
  if (Array.isArray(body.messages)) return body.messages;
  if (Array.isArray(body.history)) return body.history;
  if (requestText) return [{ role: 'user', content: requestText }];
  return [];
}

function buildFetchArgsWithTaskId(args, taskId) {
  const [resource, options = {}] = args;
  if (typeof resource === 'string') {
    return [appendTaskIdToUrl(resource, taskId), options];
  }
  if (resource instanceof Request) {
    const newUrl = appendTaskIdToUrl(resource.url, taskId);
    const newRequest = new Request(newUrl, {
      method: resource.method,
      headers: resource.headers,
      body: resource.body,
      mode: resource.mode,
      credentials: resource.credentials,
      cache: resource.cache,
      redirect: resource.redirect,
      referrer: resource.referrer,
      integrity: resource.integrity,
      keepalive: resource.keepalive,
      signal: resource.signal,
    });
    return [newRequest, undefined];
  }
  return args;
}

export function initAiFetchTracker() {
  if (typeof window === 'undefined' || window.__aiFetchTracker) return;
  window.__aiFetchTracker = true;

  const originalFetch = window.fetch;

  window.fetch = async function (...args) {
    const [resource, options = {}] = args;
    const url = typeof resource === 'string' ? resource : resource?.url;
    const method = typeof resource === 'string' ? (options.method || 'GET') : (resource?.method || 'GET');

    if (!isAiUrl(url) || method.toUpperCase() === 'GET') {
      return originalFetch(...args);
    }

    const body = await extractBody(resource, options);
    const channelId = resolveChannel(url, body);
    const channel = getChannelInfo(channelId);
    const requestText = getRequestText(body);
    const isStream = isStreamRequest(options.headers, body);

    const task = createTask({
      channelId,
      channelName: channel.name,
      channelColor: channel.color,
      request: requestText,
      url,
      context: buildContextFromBody(body),
      history: buildHistoryFromBody(body, requestText),
    });

    const taskId = task.id;
    updateTask(taskId, { status: 'running' });

    const newArgs = buildFetchArgsWithTaskId(args, taskId);

    try {
      const response = await originalFetch(...newArgs);
      if (!isStream) {
        const responseText = await safeReadResponseText(response);
        const responsePayload = responseText ? {
          response: responseText,
          status: 'completed',
          outputs: responseText,
          history: [...(task.history || []), { role: 'assistant', content: responseText }],
        } : { status: 'completed' };
        updateTask(taskId, responsePayload);
      } else {
        updateTask(taskId, { status: 'completed' });
      }
      return response;
    } catch (error) {
      const status = error?.name === 'AbortError' ? 'cancelled' : 'failed';
      updateTask(taskId, {
        status,
        error: error.message || String(error),
        response: status === 'cancelled' ? 'Annulée' : `Erreur: ${error.message || error}`,
      });
      throw error;
    }
  };
}
